/**
 * Canvas Save & Resume — persists stroke data to Firebase Storage as JSON,
 * with a lightweight RTDB index for listing saved canvases.
 *
 * Storage is ~$0.026/GB/month — a typical canvas save is 50–500KB,
 * so thousands of saves cost cents per month.
 *
 * Flow:
 *   Save: capture strokes + thumbnail → upload JSON + JPEG to Storage →
 *         write index entry to RTDB
 *   Load: read JSON from Storage → parse → restore strokes to canvas + RTDB
 */

import { ref as storageRef, uploadString, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { ref as dbRef, push, set, get, remove } from 'firebase/database';
import { storage, database } from '@/firebase';

const SAVE_VERSION = 1;

/**
 * Save the current canvas (all strokes + background) to Firebase Storage.
 * Also uploads a thumbnail and writes an index entry to RTDB.
 *
 * @param {Object} params
 * @param {string} params.roomCode - Current room code
 * @param {Array}  params.strokes  - canvas.allStrokes array
 * @param {string} params.background - canvas background colour
 * @param {string} params.userId - Current user ID
 * @param {string} params.nickname - Current user nickname
 * @param {Object} params.stageRef - Konva stage ref for thumbnail capture
 * @param {string} [params.name] - Optional name for the save
 * @returns {Promise<{success: boolean, saveId?: string, error?: string}>}
 */
export async function saveCanvas({ roomCode, strokes, background, userId, nickname, stageRef, name }) {
  if (!roomCode || !strokes || strokes.length === 0) {
    return { success: false, error: 'Nothing to save — canvas is empty' };
  }

  try {
    const saveId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const saveName = name || `Canvas ${new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;

    // Strip pathData (recomputed on load) and any undefined values from strokes.
    // This keeps the JSON lean — pathData is the biggest field and is derived.
    const cleanedStrokes = strokes.map((stroke) => {
      const { pathData, ...rest } = stroke;
      const cleaned = {};
      for (const [key, value] of Object.entries(rest)) {
        if (value !== undefined && value !== null) cleaned[key] = value;
      }
      return cleaned;
    });

    const saveData = {
      version: SAVE_VERSION,
      savedAt: Date.now(),
      savedBy: nickname,
      savedByUserId: userId,
      roomCode,
      background,
      strokeCount: cleanedStrokes.length,
      name: saveName,
      strokes: cleanedStrokes,
    };

    // Upload JSON to Storage
    const jsonBlob = new Blob([JSON.stringify(saveData)], { type: 'application/json' });
    const jsonPath = `canvases/${roomCode}/${saveId}.json`;
    const jsonRef = storageRef(storage, jsonPath);
    await uploadBytes(jsonRef, jsonBlob);
    const jsonUrl = await getDownloadURL(jsonRef);

    // Upload thumbnail
    let thumbnailUrl = null;
    const stage = stageRef?.current;
    if (stage) {
      const thumbDataUrl = stage.toDataURL({ pixelRatio: 0.3, mimeType: 'image/jpeg', quality: 0.6 });
      const thumbPath = `canvases/${roomCode}/${saveId}_thumb.jpg`;
      const thumbRef = storageRef(storage, thumbPath);
      await uploadString(thumbRef, thumbDataUrl, 'data_url');
      thumbnailUrl = await getDownloadURL(thumbRef);
    }

    // Write index entry to RTDB (lightweight — for listing without downloading the JSON)
    const indexRef = dbRef(database, `savedCanvases/${roomCode}/${saveId}`);
    await set(indexRef, {
      name: saveName,
      savedAt: Date.now(),
      savedBy: nickname,
      savedByUserId: userId,
      strokeCount: cleanedStrokes.length,
      background,
      thumbnailUrl,
      jsonUrl,
      storagePath: jsonPath,
    });

    return { success: true, saveId };
  } catch (err) {
    console.error('[saveCanvas] Failed:', err);
    return { success: false, error: err?.message || 'Failed to save canvas' };
  }
}

/**
 * Load a saved canvas — fetches the JSON from Storage and returns the strokes.
 *
 * @param {string} jsonUrl - Download URL for the save JSON
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function loadCanvas(jsonUrl) {
  if (!jsonUrl) return { success: false, error: 'No save URL provided' };

  try {
    const response = await fetch(jsonUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();

    if (!data.strokes || !Array.isArray(data.strokes)) {
      return { success: false, error: 'Invalid save file — no stroke data found' };
    }

    return { success: true, data };
  } catch (err) {
    console.error('[loadCanvas] Failed:', err);
    return { success: false, error: err?.message || 'Failed to load canvas' };
  }
}

/**
 * List all saved canvases for a room (reads the RTDB index).
 *
 * @param {string} roomCode
 * @returns {Promise<Array>} sorted by savedAt descending
 */
export async function listSavedCanvases(roomCode) {
  if (!roomCode) return [];
  try {
    const snap = await get(dbRef(database, `savedCanvases/${roomCode}`));
    if (!snap.exists()) return [];
    const data = snap.val();
    return Object.entries(data)
      .map(([id, val]) => ({ id, ...val }))
      .sort((a, b) => b.savedAt - a.savedAt);
  } catch (err) {
    console.error('[listSavedCanvases] Failed:', err);
    return [];
  }
}

/**
 * Delete a saved canvas (removes JSON + thumbnail from Storage, index from RTDB).
 *
 * @param {string} roomCode
 * @param {string} saveId
 * @param {string} storagePath - Storage path of the JSON file
 */
export async function deleteSavedCanvas(roomCode, saveId, storagePath) {
  try {
    // Delete from Storage
    if (storagePath) {
      await deleteObject(storageRef(storage, storagePath)).catch(() => {});
      // Thumbnail is at same path with _thumb.jpg suffix
      const thumbPath = storagePath.replace('.json', '_thumb.jpg');
      await deleteObject(storageRef(storage, thumbPath)).catch(() => {});
    }
    // Remove RTDB index entry
    await remove(dbRef(database, `savedCanvases/${roomCode}/${saveId}`));
  } catch (err) {
    console.warn('[deleteSavedCanvas] Failed:', err);
  }
}
